import React, { useEffect, useState } from 'react';
import {
  Route,
  Switch,
  withRouter,
  useHistory,
  useLocation,
} from 'react-router-dom';
import { Helmet } from 'react-helmet';
import { useDispatch, useSelector } from 'react-redux';
import ReactGA from 'react-ga';
import axios from 'axios';
import { API_URL, isMMI } from './config';

import Root from './Root';
import AppLayout from './layouts/App';
import NotFound from './containers/NotFound';
import routes from './routes';
import { signOut, impersonateInit } from './store/actions/auth';
import { fetchTerms } from './store/actions/course';
import { makeSelectUser, makeSelectIsImpersonating } from './store/selectors';
import { checkIfAdmin } from './service/auth';
import './css/App.css';
import { isEnterprise } from './config';
import TopAlert from './components/alerts/TopAlert';

const TRACKING_ID =
  process.env.REACT_APP_ENV === 'production' ? 'UA-70607001-1' : '';
ReactGA.initialize(TRACKING_ID);
ReactGA.pageview(window.location.pathname + window.location.search);

const authPaths = ['/forgot-password', '/reset-password'];

function App() {
  const dispatch = useDispatch();
  const history = useHistory();
  const location = useLocation();
  const user = useSelector(makeSelectUser());
  const _isImpersonating = useSelector(makeSelectIsImpersonating());
  const isImpersonating = !!localStorage.getItem('admin_impersonate');
  const searchParams = new URLSearchParams(location.search);
  const emailToImpersonate = searchParams.get('impersonate');
  const token = searchParams.get('token');
  const [validating, setValidating] = useState(true);
  const [topAlertData, setTopAlertData] = useState(null);
  useEffect(() => {
    async function fetchData() {
      try {
        const response = await axios.get(
          `${API_URL}/topalert/listlast`
          // 'http://localhost:3005/api/topalert/listlast'
        );
        setTopAlertData(response.data);
      } catch (error) {
        console.error(error);
      }
    }

    fetchData();
  }, []);

  useEffect(() => {
    dispatch(fetchTerms());

    if (emailToImpersonate && token) {
      validateAdminToken(token, emailToImpersonate.replaceAll(' ', '+'));
    } else {
      setValidating(false);
    }
  }, []);

  useEffect(() => {
    if (
      user &&
      window.Rollbar &&
      typeof window.Rollbar.configure === 'function'
    ) {
      window.Rollbar.configure({
        payload: {
          person: {
            id: user._id,
            email: user.email,
            username: user.name,
          },
        },
      });
    }
  }, [user]);

  useEffect(() => {
    if (!user && !validating && !location.pathname.includes('/certification')) {
      if (authPaths.some((path) => location.pathname.includes(path))) return;

      const redirect =
        location.pathname !== '/sign-in'
          ? `?redirect=${encodeURIComponent(location.pathname)}`
          : '';
      history.push(`/sign-in${isImpersonating ? '' : redirect}`);
    }
  }, [user, history, validating]);

  const validateAdminToken = async (token, email) => {
    setValidating(true);
    try {
      const res = await checkIfAdmin(token);

      setValidating(false);
      if (res.data.data.isAdmin) {
        dispatch(impersonateInit({ token, email }));
      }
    } catch (err) {
      setValidating(false);
      return false;
    }
  };

  return (
    <>
      {!isMMI && topAlertData && topAlertData.data && topAlertData.data[0].turnedOn && (
        <TopAlert />
      )}
      <AppLayout>
        {isEnterprise && (
          <Helmet
            title={
              isEnterprise
                ? window.location.host.split('.')[0] === 'icsc'
                  ? 'ICSC Retail Real Estate Modeling - Powered by A.CRE'
                  : 'A.CRE Accelerator for Enterprise - Real Estate Financial Modeling Training'
                : 'A.CRE Accelerator - Real Estate Financial Modeling Training'
            }
            meta={[
              {
                name: 'description',
                content:
                  window.location.host.split('.')[0] === 'icsc'
                    ? 'A custom online retail real estate financial modeling training program for ICSC student members.'
                    : `${isEnterprise
                      ? 'A.CRE Accelerator for Enterprise'
                      : 'A.CRE Accelerator'
                    }, a comprehensive real estate financial modeling training for development, acquisitions, brokerage, asset management, and other professionals.`,
              },
            ]}
            link={[
              {
                rel: 'icon',
                type: 'image/png',
                href:
                  window.location.host.split('.')[0] === 'icsc'
                    ? 'icsc-favicon.png'
                    : 'favicon.png',
              },
            ]}
          />
        )}
        {_isImpersonating ? (
          <div>Loading... Wait for a sec...</div>
        ) : (
          <Switch>
            <Route
              exact
              path={Object.keys(routes).map((k) => routes[k])}
              component={Root}
            />
            <Route path="*" component={NotFound} />
          </Switch>
        )}
      </AppLayout>
    </>
  );
}

export const LocationDisplay = withRouter(({ location }) => (
  <div data-testid="location-display">{location.pathname}</div>
));

export default App;
